import React, { useEffect, useMemo, useState } from "react"
import { useQuery, useQueryClient } from "react-query"
import axios, { AxiosError } from "axios"
import { toast } from "react-toastify"

import { ICandidatSuiviDocumentModel, ICandidatSuiviModel } from "@common-models/*"
import { getSuivis } from "../../core/_requests"
// Import de l'endpoint backend
import { useAuth } from "../../../../../AppContext"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyAlert from "@common-utils/MyAlert"

import CandidatSuiviEditForm from "../../candidatSuivi/_CandidatSuiviEditForm"
import MyGoogleDriveFileViewerInModal from "../../../../../components/MyDocumentViewer/MyGoogleDriveFileViewerInModal"
import CandidatSuiviFilterBar from "./CandidatSuiviFilterBar"
import CandidatSuiviTimelineItem from "./CandidatSuiviTimelineItem"
import { CONST_API_RECRUITMENT_CANDIDATES_SUIVI_UPDATE_FEELING_ENDPOINT } from "../../../../../endpoints"


export const CandidatSuiviListing = ({ candidat_id, onLoad = () => {}, preFilterTypes = [] }: { candidat_id: number; onLoad?: () => void; preFilterTypes?: string[] }) => {
     const queryClient = useQueryClient()
     const { currentUser } = useAuth()

     const [someItemBeingEdited, setSomeItemBeingEdited] = useState<ICandidatSuiviModel | null>(null)
     const [activeFilters, setActiveFilters] = useState<string[]>(preFilterTypes || [])
     const [suiviDocumentInModal, setSuiviDocumentInModal] = useState<ICandidatSuiviDocumentModel | null>(null)

     const preFiltersKey = JSON.stringify(preFilterTypes)
     useEffect(() => {
          setActiveFilters(preFilterTypes || [])
     }, [preFiltersKey])

     const { data, isLoading, error } = useQuery<ICandidatSuiviModel[], AxiosError>([REACT_QUERY_KEY_GET_SUIVI_LISTING_QUERY, candidat_id], async () => {
          try {
               return (await getSuivis(candidat_id)).data
          } catch (e: any) {
               toast.error(e.response?.data?.detail || "Erreur lors de la récupération du suivi")
               throw e
          } finally {
               onLoad()
          }
     })

     // --- Logique de mise à jour du Feeling raccordée au Backend ---
     const handleUpdateFeeling = async (item: ICandidatSuiviModel, feeling: string | null, comment?: string) => {
          const oldData = queryClient.getQueryData<ICandidatSuiviModel[]>([REACT_QUERY_KEY_GET_SUIVI_LISTING_QUERY, candidat_id])

          queryClient.setQueryData(
               [REACT_QUERY_KEY_GET_SUIVI_LISTING_QUERY, candidat_id],
               (prev: any) =>
                    prev?.map((i: ICandidatSuiviModel) => {
                         if (i.id !== item.id) return i

                         // Filtrage correct
                         const otherFeelings = i.feelings?.filter(f => f.personnel?.id !== currentUser?.personnel?.id) || []

                         const newMyFeeling = feeling
                              ? [
                                     {
                                          feeling,
                                          commentaire: comment,
                                          personnel: currentUser?.personnel, // Assure que l'ID est présent pour le prochain find()
                                          createdAt: require("moment")(),
                                     },
                                ]
                              : []

                         return { ...i, feelings: [...otherFeelings, ...newMyFeeling] }
                    })
          )

          try {
               const url = CONST_API_RECRUITMENT_CANDIDATES_SUIVI_UPDATE_FEELING_ENDPOINT(item.id!)
               await axios.post(url, { feeling, feelingCommentaire: comment })
               toast.success(feeling ? "Avis enregistrée" : "Avis supprimé")
          } catch (e) {
               queryClient.setQueryData([REACT_QUERY_KEY_GET_SUIVI_LISTING_QUERY, candidat_id], oldData)
               toast.error("Erreur réseau : impossible de mettre à jour le sentiment")
          }
     }

     const filteredData = useMemo(() => {
          if (!data) return []
          return activeFilters.length === 0 ? data : data.filter(item => activeFilters.includes(item.type!))
     }, [data, activeFilters])

     if (isLoading)
          return (
               <div className="d-flex justify-content-center py-10">
                    <MySimpleSpinner size="xl" />
               </div>
          )
     if (error) return <MyAlert type="danger">{error.response?.data.detail}</MyAlert>

     if (someItemBeingEdited)
          return (
               <div className="animate__animated animate__fadeIn">
                    <div className="d-flex justify-content-between align-items-center mb-6">
                         <h3 className="fw-bolder m-0">Modification du suivi</h3>
                         <button className="btn btn-sm btn-light" onClick={() => setSomeItemBeingEdited(null)}>
                              Retour
                         </button>
                    </div>
                    <CandidatSuiviEditForm
                         id={someItemBeingEdited.id as number}
                         handleSubmitCallBack={mod => {
                              queryClient.setQueryData([REACT_QUERY_KEY_GET_SUIVI_LISTING_QUERY, candidat_id], (prev: any) => prev?.map((i: any) => (i.id === mod.id ? mod : i)))
                              setSomeItemBeingEdited(null)
                         }}
                    />
               </div>
          )

     return (
          <div className="card-body p-0 d-flex flex-column w-100 px-5">
               <div className="w-100 mx-auto" style={{ maxWidth: "900px" }}>
                    <CandidatSuiviFilterBar
                         activeFilters={activeFilters}
                         onToggle={id => setActiveFilters(prev => (prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]))}
                         onClear={() => setActiveFilters([])}
                    />

                    {filteredData.length === 0 ? (
                         <div className="text-center py-10 text-muted fs-6 fw-bold">Aucun historique de suivi correspondant.</div>
                    ) : (
                         filteredData.map((item, index) => (
                              <CandidatSuiviTimelineItem
                                   key={item.id || index}
                                   item={item}
                                   isLast={index === filteredData.length - 1}
                                   isAuthor={currentUser?.teamRecrutement?.some(t => t.id === item.personnel?.id) || false}
                                   onEdit={setSomeItemBeingEdited}
                                   onViewDoc={setSuiviDocumentInModal}
                                   onUpdateFeeling={handleUpdateFeeling}
                              />
                         ))
                    )}
               </div>

               {suiviDocumentInModal && (
                    <MyGoogleDriveFileViewerInModal
                         googleDriveID={suiviDocumentInModal.googleDriveID!}
                         googleDriveMimetype={suiviDocumentInModal.googleDriveMimeType!}
                         modalTitle={<span>Pièce jointe</span>}
                         handleCloseModal={() => setSuiviDocumentInModal(null)}
                    />
               )}
          </div>
     )
}

export const REACT_QUERY_KEY_GET_SUIVI_LISTING_QUERY = "REACT_QUERY_KEY_GET_SUIVI_LISTING_QUERY"
